<template>
	<div class="TicketOption" v-if="modelValue"
		:class="{ error: !valid }"
	>
		<v-btn class="removeRow" v-if="allowDelete" @click="$emit('delete', modelValue)">
			<v-icon color="white">mdi-delete</v-icon>
		</v-btn>
		<div class="dragHandle" v-if="draggable"
			:title="$t('text.dragToReorderTicketOption')"
			:aria-label="$t('text.dragToReorderTicketOption')"
			:data-cy="'dragTicketOption_' + dataCyIndex"
		>
			<v-icon size="small">mdi-drag-horizontal-variant</v-icon>
		</div>
		<div class="row">
			<!-- TODO: hasField? -->
			<!-- TODO: no left border - does field have a mode for this? -->
			<Field :title="$t('text.ticketType')" required>
				<TicketTypeField v-model="modelValue.fields.ticketType.de"
					:error="ticketTypeError" 
					:disabled="productIsExternal"
					:data-cy="'ticketType_' + dataCyIndex"
				/>
			</Field>
			<Field :title="$t('text.reduction')" required>
				<ReductionField v-model="modelValue.fields.reduction.de"
					:disabled="productIsExternal"
					:error="reductionError" 
					:data-cy="'reduction_' + dataCyIndex"
				/>
			</Field>
		</div>
		<div class="row">
			<!-- TODO: model driven field on price -->
			<Field :title="$t('text.price')" required>
				<v-text-field v-model="modelValue.fields.price.de"
					type="number" hide-details variant="outlined" density="compact"
					:min="0"
					:error-messages="priceErrors"
					:disabled="productIsExternal"
					:data-cy="'price_' + dataCyIndex"
					style="background-color: #ffffff;"
				/>
			</Field>
			<div class="price-switches" style="display: flex; align-items: center; gap: 30px;">
				<div class="from-price-switch stays-editable-when-locked">
					<mys-switch
						v-if="hasField('ticketInfo', 'fromPrice', product)"
						v-model="modelValue.fields.fromPriceOption.de"
						@update:modelValue="$emit('fromPriceChanged', $event)"
						:label="$t('text.fromPrice')"
					/>
				</div>
				<IfFeatureFlag flag="FEATURE_TRADE_HUB">
					<template v-if="hasField('ticketInfo', 'priceType', product) && featureEnabled(Features.TRADE_HUB) && featureEnabled(Features.TRADE_HUB_PRICE_TYPE)">
						<div class="group-price-switch">
							<RadioSelect v-model="modelValue.fields.priceType.de" :options="priceTypeOptions"
								:label="`${$t('text.priceType')} (${$t('text.required')})`"
								:helpText="$t('text.priceTypeHelp')"
								helpTextTooltip
								:placeholder="`${$t('text.choosePriceType')}...`"
								dataCy="priceType"
							/>
							<p v-if="tourGuidePriceTypeError" class="field-error">{{ tourGuidePriceTypeError }}</p>
						</div>
					</template>
				</IfFeatureFlag>
			</div>
		</div>
		<div class="row">
			<Field v-if="hasField('ticketInfo', 'minimumTickets', product)" :title="$t('text.minTickets')" required>
				<!-- TODO: model driven field -->
				<v-text-field v-model="modelValue.fields.minimumTickets.de"
					type="number" variant="outlined" density="compact"
					:min="1"
					:hide-details="!minimumTicketsErrors.length"
					:error-messages="minimumTicketsErrors"
					:disabled="productIsExternal"
				/>
				<!--
					@keypress="isNaturalNumber($event)"
				-->
			</Field>
			<Field v-if="hasField('ticketInfo', 'maximumTickets', product)" :title="$t('text.maxTickets')" required>
				<v-text-field v-model="modelValue.fields.maximumTickets.de"
					hide-details variant="outlined" density="compact"
					type="number"
					min="1"
					:error-messages="maximumTicketsErrors"
					:disabled="productIsExternal"
					@keypress="isNaturalNumber($event)"
				/>
			</Field>
		</div>
		<div class="row">
			<Field v-if="hasField('ticketInfo', 'contingentQuantity', product)" :title="$t('text.noOfPeople')"
				:helpText="dataCyIndex == 0 ? $t('text.ticketsDesc') : undefined"
				required
			>
				<v-text-field v-model="modelValue.fields.contingentQuantity.de"
					variant="outlined" density="compact" :hide-details="!contingentQuantityErrors.length" style="background-color:#ffffff"
					type="number" min="1" max="255"
					:disabled="productIsExternal"
					:error-messages="contingentQuantityErrors"
					@keypress="isNaturalNumber($event)"
				/>
			</Field>
		</div>

		<div v-if="featureEnabled('ticket-external-system-assignment')" class="row">
			<div class="ticketExternalSystemAssignment">
				<div>
					<v-checkbox density="compact" hide-details v-model="hasTicketExternalSystemAssignment" />
					<p style="width: fit-content;">{{ $t('text.ticketExternalSystemAssignmentEnabled') }}</p>
				</div>
				<Field v-if="hasField('ticketInfo', 'ticketExternalSystemAssignment', modelValue) && hasTicketExternalSystemAssignment" :title="$t('text.ticketExternalSystemAssignment')" required>
					<v-text-field hide-details variant="outlined" density="compact" type="text"
						v-model="modelValue.fields.ticketExternalSystemAssignment.de" 
						:error-messages="ticketExternalSystemAssignmentErrors"
					/>
				</Field>
			</div>
		</div>
		<div v-if="externalSystem && hasField('ticketInfo','externalSystem', product)" class="externalSystem">
			<v-label>{{ $t(`text.ticketOption${externalSystem}SpecificTitle`) }}</v-label>
			<p class="helpText">{{ $t(`text.ticketOption${externalSystem}SpecificHelpText`) }}</p>
			<TicketOptionExternalSystemSkidata v-if="externalSystem === EXTERNAL_SYSTEM.SKIDATA" :ticketOption="modelValue" ref="extSkidata" />
			<TicketOptionExternalSystemAxess v-if="externalSystem === EXTERNAL_SYSTEM.AXESS" :ticketOption="modelValue" ref="extAxess" />
			<TicketOptionExternalSystemSbbv3 v-if="externalSystem === EXTERNAL_SYSTEM.SBBV3" :ticketOption="modelValue" :product="product" ref="extSbbv3" />
			<TicketOptionExternalSystemSmeetz v-if="externalSystem === EXTERNAL_SYSTEM.SMEETZ" :ticketOption="modelValue" ref="extSmeetz" />
			<TicketOptionExternalSystemInfoSys v-if="externalSystem === EXTERNAL_SYSTEM.INFOSYSTEM" :ticketOption="modelValue" :product="product" ref="extInfoSys" />
		</div>
	</div>
</template>

<script lang="ts">
import Common from '../../mixins/Common.vue'
import TicketTypeField from './TicketTypeField.vue'
import ReductionField from './ReductionField.vue'
import SalesChannelSelector from '../clientAssignment/SalesChannelSelector.vue'
import TicketOptionExternalSystemSkidata from './TicketOptionExternalSystemSkidata.vue'
import TicketOptionExternalSystemAxess from './TicketOptionExternalSystemAxess.vue'
import TicketOptionExternalSystemSbbv3 from './TicketOptionExternalSystemSbbv3.vue'
import TicketOptionExternalSystemSmeetz from './TicketOptionExternalSystemSmeetz.vue'
import TicketOptionExternalSystemInfoSys from './TicketOptionExternalSystemInfoSys.vue'
import FieldSet from '../../views/applications/packageDesigner/FieldSet.vue'
import IfFeatureFlag from '../ifFeatureFlag/IfFeatureFlag.vue'
import { EXTERNAL_SYSTEM } from '../../constants'
import ChildErrorDispatcher from '../../views/applications/packageDesigner/ChildErrorDispatcher.vue'
import RadioSelect from '@/components/common/RadioSelect.vue'
import { PriceType } from '../../constants'
import { Features } from '../../../../shared/features'

export default {
	mixins: [ Common, ChildErrorDispatcher ],
	components: { FieldSet, TicketTypeField, ReductionField, TicketOptionExternalSystemSkidata, TicketOptionExternalSystemAxess, TicketOptionExternalSystemSbbv3, TicketOptionExternalSystemSmeetz, TicketOptionExternalSystemInfoSys, SalesChannelSelector, IfFeatureFlag, RadioSelect },
	props: {
		modelValue: Object, // TicketOption
		product: Object,
		// TODO: is this needed or is this basically a full disable?
		productIsExternal: Boolean,
		dataCyIndex: Number,
		allowDelete: Boolean,
		draggable: Boolean,
		duplicateCombination: { type: Boolean, default: false },
		externalSystem: {
			type: String,
			required: true,
			validator: (value: string) => Object.values(EXTERNAL_SYSTEM).includes(value as any),
		},
		ticketOptionAssignment: Object
	},
	data: () => ({
		hasAssignments: false,
		EXTERNAL_SYSTEM: EXTERNAL_SYSTEM,
		Features: Features,
		hasTicketExternalSystemAssignment: false,
		ticketExternalSystemAssignmentValue: '',
	}),
	computed: {
		priceTypeOptions() {
			return [
				{
					title: this.$t('text.priceTypeFit'),
					value: PriceType.FIT,
				},
				{
					title: this.$t('text.priceTypeGroup'),
					value: PriceType.GROUP,
				},
				{
					title: this.$t('text.priceTypeGeneric'),
					value: PriceType.GENERIC,
				},
			]
		},
		selectedClient() {
			return this.$store.state.selectedClient
		},
		ticketTypeError() {
			if (this.productIsExternal) return false
			return !this.modelValue?.fields?.ticketType?.de?.fields
				|| !Object.keys(this.modelValue.fields.ticketType.de).length
		},
		reductionError() {
			if (this.productIsExternal) return false
			return !this.modelValue?.fields?.reduction?.de?.fields
				|| !Object.keys(this.modelValue.fields.reduction.de).length
				|| (!this.modelValue?.fields?.priceType?.de && this.modelValue?.fields?.reduction?.de?.fields?.freeTourGuide?.de)
		},
		// TODO: instead of these validations we should use model driven fields and ChildErrorReceiver
		minimumTicketsErrors() {
			if (this.productIsExternal) return []
			const errors = []
			if (!this.modelValue?.fields?.minimumTickets?.de) errors.push(this.$t('text.minTicketsRequired'))
			if (this.modelValue?.fields?.minimumTickets?.de > this.modelValue?.fields?.maximumTickets?.de) errors.push(this.$t('text.minMaxTicketsError'))
			return errors
		},
		maximumTicketsErrors() {
			if (this.productIsExternal) return []
			const errors = []
			if (!this.modelValue?.fields?.maximumTickets?.de) errors.push(this.$t('text.maxTicketsRequired'))
			return errors
		},
		priceErrors() {
			if (this.productIsExternal) return []
			const errors = []
			if ((this.modelValue?.fields?.price?.de !== 0 && !this.modelValue?.fields?.price?.de) || this.modelValue?.fields?.price?.de === '') errors.push(this.$t('text.priceRequired'))
			return errors
		},
		tourGuidePriceTypeError() {
			if (this.productIsExternal) return ''
			if (!this.modelValue?.fields?.reduction?.de?.fields?.freeTourGuide?.de) return ''
			if (!this.modelValue?.fields?.priceType?.de || this.modelValue.fields.priceType.de === PriceType.GROUP) return ''
			return this.$t('text.tourGuideRequiresGroupPriceType')
		},
		contingentQuantityErrors() {
			if (this.productIsExternal) return []
			const errors = []
			const contingentQuantity = this.modelValue?.fields?.contingentQuantity?.de
			if (!contingentQuantity) errors.push(this.$t('text.noOfPeopleRequired'))
			if (Number(contingentQuantity) > 255) errors.push(this.$t('text.field-val-rangeMaxContingent', { max: 255 }))
			return errors
		},
		ticketExternalSystemAssignmentErrors() {
			if (this.productIsExternal) return []
			const errors = []
			if (this.hasTicketExternalSystemAssignment && !this.modelValue?.fields?.ticketExternalSystemAssignment?.de) errors.push(this.$t('text.ticketExternalSystemAssignmentRequired'))
			return errors
		},
		valid() {
			// TODO: we should change this whole validation to model driven fields
			// with this trick we retrigger on child input
			this.modelValue?.fields?.external?.dtaProductId
			this.modelValue?.fields?.external?.ticketTypeNumber
			this.modelValue?.fields?.external?.id1
			return true
				&& !this.ticketTypeError
				&& !this.reductionError
				&& !this.minimumTicketsErrors.length
				&& !this.priceErrors.length
				&& !this.contingentQuantityErrors.length
				&& !this.ticketExternalSystemAssignmentErrors.length
				&& !!this.modelValue?.fields?.minimumTickets?.de
				&& !!this.modelValue?.fields?.maximumTickets?.de
				&& !!this.modelValue?.fields?.contingentQuantity?.de
				&& (this.$refs.extSkidata?.valid ?? true)
				&& (this.$refs.extAxess?.valid ?? true)
				&& (this.$refs.extSbbv3?.valid ?? true)
				&& (this.$refs.extSmeetz?.valid ?? true)
				&& (this.$refs.extInfoSys?.valid ?? true)
				&& !this.tourGuidePriceTypeError
		},
	},
	watch: {
		ticketOptionAssignment() {
			if (this.ticketOptionAssignment && Object.keys(this.ticketOptionAssignment).length) {
				this.hasAssignments = true
			}
		},
		hasAssignments() {
			if (!this.hasAssignments) {
				this.$emit('removeTicketOptionAssignment')
			}
		},
		ticketExternalSystemAssignmentValue(value) {
			if (!this.modelValue) return
			this.modelValue.fields.ticketExternalSystemAssignment.de = value
		},
		hasTicketExternalSystemAssignment(value) {
			if (!value) {
				this.ticketExternalSystemAssignmentValue = ''
			}
		},
	},
	methods: {
		// TODO: i have just taken this over from the old TicketInfo.vue - is this ok?
		isDecimal: function (evt) {
			evt = (evt) ? evt : window.event;
			var charCode = (evt.which) ? evt.which : evt.keyCode

			if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 44) {
				evt.preventDefault()
				return false
			}
			return true
		},
		updateAssignment(value) {
			this.$emit('updateTicketOptionAssignment', value)
		},
	},
	created() {
		if (this.modelValue?.fields && !this.modelValue?.fields?.fromPriceOption) {
			this.modelValue.fields.fromPriceOption = { de: false }
		}
	},
	mounted() {
		// TODO: expired products seem to have prices like "15,00" set and our number fields do not accept this
		const price = this.modelValue?.fields?.price?.de
		if (price && typeof price === 'string') {
			this.modelValue.fields.price.de = parseFloat(price.replace(',', '.'))
		}
		if (this.ticketOptionAssignment && Object.keys(this.ticketOptionAssignment).length) {
			this.hasAssignments = true
		}
		if (this.modelValue?.fields?.ticketExternalSystemAssignment?.de?.length) {
			this.ticketExternalSystemAssignmentValue = this.modelValue.fields.ticketExternalSystemAssignment.de
			this.hasTicketExternalSystemAssignment = true
		}
	},
}
</script>

<style scoped>
.TicketOption { position: relative; display: flex; flex-direction: column; gap: 8px; background: #fafafa; padding: 12px; border-radius: 8px; border: 1px solid #ddd; }
.dragHandle { align-self: flex-start; display: flex; align-items: center; justify-content: center; width: 48px; height: 20px; border-radius: 4px; background: #eeeeee; color: #666666; cursor: grab; }
.dragHandle:active { cursor: grabbing; }
.row { display: flex; gap: 10px; width: 100%; }
.row > * { flex: 1 0; }
.row > .Field { border-left: none; padding: 0; }
.externalSystem { border-top: 1px solid #ddd; padding-top: 12px; }
.error { outline: 2px solid red; outline-offset: -1px; }
.field-error { color: #b00020; font-size: 12px; line-height: 1.3; margin: 4px 0 0; }
.v-text-field { background-color: #ffffff; }
</style>
<style lang="scss" scoped>
.TicketOption {
	.salesChannelAssignment,
	.ticketExternalSystemAssignment {
		> div {
			display: flex;
			align-items: center;
			> p {
				margin-left: 8px;
			}
		}
	}
}
</style>
